import { Component, ViewChild, NgZone, HostListener, ChangeDetectorRef, Injector } from "@angular/core";
import { Platform, NavController } from "@ionic/angular";
import { Events } from '@omni/events';
import { StatusBar } from "@awesome-cordova-plugins/status-bar/ngx";
import { LoginPage } from "./pages/login/login";
import { APP_VERSION } from "../config/endpoints.config";
import { Deeplinks } from '@awesome-cordova-plugins/deeplinks/ngx';
import { RepServices } from "./data-services/rep/rep.services";
import { DEVICE_ORIENTATION, DeviceService } from "./services/device/device.service";
import { TranslateService } from '@ngx-translate/core';
import { LocalizationService } from './services/localization/localization.service';
import { DateTimeFormatsService } from "./services/date-time-formats/date-time-formats.service";
// importing locale for date localization
import { registerLocaleData } from '@angular/common';
import fr from '@angular/common/locales/fr';
import es from '@angular/common/locales/es';
import en from '@angular/common/locales/en';
import ja from '@angular/common/locales/ja';
import de from '@angular/common/locales/de';
import nl from '@angular/common/locales/nl';
import tr from '@angular/common/locales/tr';
import pt from '@angular/common/locales/pt';
import it from '@angular/common/locales/it';
import zh from '@angular/common/locales/zh';
import zh_CN from '@angular/common/locales/zh';
import { LoginService } from './services/msal/login.service';
import { TranscriptionSweepService } from '@omni/services/speech/transcription-sweep.service';
import { OrderSyncNoticesService } from '@omni/services/order-capture/order-sync-notices.service';
import { electronApi } from './services/electron-api';
import { HomePage } from './pages/home/home';
import { VoiceNoteService } from '@omni/services/voice-note/voice-note.service';
import { GoAppNavigationService } from '@omni/services/go-app-navigation.service';
import { CordovaPlugin } from './services/cordova';
import { readSafeAreaInsets } from './utility/safe-area-insets';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { AiToolRegistryService } from './ai/tools/tool-registry.service';
import { register } from 'swiper/element/bundle';

register();

declare global {
  interface Window { MyNamespace: any; }
}

@Component({
  selector: "app-root",
  templateUrl: "app.html",
})
export class MyApp {
  rootPage: any;
  @ViewChild("navigate", { static: true }) nav: NavController;
  recognizer: any;
  private _ready: Promise<void>;

  orientationPP = false;
  orientationPS = false;
  orientationLP = false;
  orientationLS = false;

  // Defer mounting the global Nova harness until after bootstrap so it does not
  // construct during the app's initial change-detection pass.
  novaReady = false;

  private _cordovaHelpers = new CordovaPlugin('CordovaHelpers');
  constructor(
    private platform: Platform,
    private statusBar: StatusBar,
    private deeplinking: Deeplinks,
    private events: Events,
    private translate: TranslateService,
    private localizationService: LocalizationService,
    private dateTimeFormatsService: DateTimeFormatsService,
    public repService: RepServices,
    public deviceService: DeviceService,
    private cd: ChangeDetectorRef,
    private loginService: LoginService,
    private transcriptionSweep: TranscriptionSweepService,
    private orderSyncNotices: OrderSyncNoticesService,
    private orientationService: ScreenOrientation,
    private injector: Injector,
    private voiceNoteService: VoiceNoteService,
    private goAppNavigationService: GoAppNavigationService,
  ) {
    console.warn(`Current Version: ${APP_VERSION}`);
  }

  ngOnInit() {
    this._ready = this.initialize();
    setTimeout(() => { this.novaReady = true; }, 0);
    let localStorageLanguage = localStorage.getItem('selectedLanguage');
    let storedDateFormat = localStorage.getItem('selectedDateFormat');
    let storedTimeFormat = localStorage.getItem('selectedTimeFormat');
    let storedSymbolPosition = localStorage.getItem('selectedSymbolPosition');
    let storedDecimalPosition = localStorage.getItem('selectedDecimalPosition');

    // Subscribe BEFORE translate.use() so the initial load (even from cache) is captured.
    // onLangChange also fires on every subsequent language switch.
    if (electronApi) {
      this.translate.onLangChange.subscribe(() => {
        electronApi.send('close-dialog-i18n', {
          title:   this.translate.instant('APP_CLOSE_CONFIRM_TITLE'),
          message: this.translate.instant('APP_CLOSE_CONFIRM_MESSAGE'),
          yes:     this.translate.instant('YES'),
          no:      this.translate.instant('NO'),
        });
      });
    }

    if (localStorageLanguage) {
      this.translate.use(localStorageLanguage);
      registerLocaleData(this.getLocaleId(localStorageLanguage));
      this.localizationService.getLanguageDetails();
    } else {
      const browserLang = typeof navigator !== 'undefined' ? navigator.language : 'en';
      const languageList = this.localizationService.getLanguages();
      const localeMap = {
        'es-ES': 'es',
        'en-US': 'en',
        'ja-JP': 'ja',
        'ja': 'ja'
      };

      const selectedLang = localeMap[browserLang] || languageList.find(lang => lang.code === browserLang)?.code || 'en';
      this.translate.use(selectedLang);
      localStorage.setItem('selectedLanguage', selectedLang);
      registerLocaleData(this.getLocaleId(selectedLang));
    }
    if (storedDateFormat) {
      this.dateTimeFormatsService.setDateFormat(JSON.parse(storedDateFormat));
    }
    if (storedTimeFormat) {
      this.dateTimeFormatsService.setTimeFormat(JSON.parse(storedTimeFormat));
    }
    if (storedSymbolPosition) {
      this.dateTimeFormatsService.setSymbolPos(JSON.parse(storedSymbolPosition));
    }
    if (storedDecimalPosition) {
      this.dateTimeFormatsService.setDecimalPos(JSON.parse(storedDecimalPosition));
    }
  }

  getClassMap() {
    return {
      'portrait-primary': this.orientationPP && this.deviceService.isApplicationLoaded,
      'portrait-secondary': this.orientationPS && this.deviceService.isApplicationLoaded,
      'landscape-primary': this.orientationLP && this.deviceService.isApplicationLoaded,
      'landscape-secondary': this.orientationLS && this.deviceService.isApplicationLoaded
    };
  }


  private getLocaleId(localeLang) {

    let localestr: any;
    switch (localeLang) {
      case 'fr':
        localestr = fr;
        break;
      case 'en':
        localestr = en;
        break;
      case 'es':
        localestr = es;
        break;
      case 'ja':
        localestr = ja;
        break;
      case 'de':
        localestr = de;
        break;
      case 'nl':
        localestr = nl;
        break;
      case 'tr':
        localestr = tr;
        break;
      case 'pt':
        localestr = pt;
        break;
      case 'it':
        localestr = it;
        break;
      case 'zh':
        localestr = zh;
        break;
      case 'zh_CN':
        localestr = zh_CN;
        break;
      default:
        localestr = en;
        break;
    }
    return localestr;
  }

  @HostListener('window:appload')
  async appLoaded() {
    await this._ready;
    (navigator as any)?.splashscreen?.hide();
    this.fitWebViewToSystemBars();

    this.setOrientationFlag(DEVICE_ORIENTATION[this.orientationService.type]);
    this.cd.detectChanges();
  }

  /**
   * On native Android, keeps the page clear of the system bars when the WebView reports no
   * safe-area insets while drawing edge to edge; the native side then lays the WebView out between
   * the bars. A native shell without the action leaves the layout unchanged.
   */
  private fitWebViewToSystemBars(): void {
    if (
      !this.deviceService.isNativeApp ||
      this.deviceService.deviceFlags.electron ||
      !this.deviceService.deviceFlags.android
    ) {
      return;
    }
    const insets = readSafeAreaInsets();
    const reportedInsets =
      insets.top + insets.right + insets.bottom + insets.left;
    this._cordovaHelpers
      .exec('fitWebViewToSystemBars', reportedInsets)
      .catch(() => undefined);
  }

  private setOrientationFlag(orientation: number) {
    this.orientationPP = this.orientationPS = this.orientationLP = this.orientationLS = false;
    if (orientation == DEVICE_ORIENTATION['portrait-primary']) {
      this.orientationPP = true;
    } else if (orientation == DEVICE_ORIENTATION['portrait-secondary']) {
      this.orientationPS = true;
    } else if (orientation == DEVICE_ORIENTATION['landscape-primary']) {
      this.orientationLP = true;
    } else if (orientation == DEVICE_ORIENTATION['landscape-secondary']) {
      this.orientationLS = true;
    }
  }

  async initialize() {
    await this.platform.ready();
    await this.loginService.ready();
    // Reading a held recording needs the tenant's speech service, so the sweep
    // starts only once login has provided it.
    this.transcriptionSweep.start();
    this.orderSyncNotices.start();
    // Okay, so the platform is ready and our plugins are available.
    // Here you can do any higher level native things you might need.
    if (this.deviceService.isNativeApp) {

      if (!this.deviceService.deviceFlags.electron) {
        if (
          this.deviceService.deviceFlags.ios ||
          this.deviceService.deviceFlags.android
        ) {
          this.statusBar.overlaysWebView(true);
          this.statusBar.backgroundColorByHexString('#00000000');
          this.statusBar.styleDefault();
        } else {
          this.statusBar.overlaysWebView(false);
          this.statusBar.styleDefault();
          this.statusBar.backgroundColorByHexString('#fff');
        }

        this.deviceService.screenOrientation.subscribe(orientation => {
          this.setOrientationFlag(orientation);
          this.cd.detectChanges();
        });
      }

      this.repService.isOfflineState$.subscribe(isOffline => {
        this.cd.detectChanges();
      });
    }

    if (this.platform.is('cordova')) {
      const handleDeeplink = ev => {
        if (ev?.$link?.path === '/voice-note/new') {
          // Open the app straight into a new voice-note capture (e.g. from a Siri
          // shortcut). Queue the capture, then route to My Notes, which starts it.
          if (this.loginService.isAuthenticated && this.voiceNoteService.isAvailable()) {
            this.voiceNoteService.queueQuickCapture();
            void this.goAppNavigationService.navigateToMenuOption({ component: 'VoiceNotesPageComponent' });
          }
          return;
        }
        let eventName = undefined;
        let eventData = undefined;
        switch (ev?.$link?.path) {
          case '/veeva/hcp-search/select':
            if (ev?.$link?.queryString?.startsWith('res=')) {
              eventName = 'select';
              eventData = atob(ev?.$link?.queryString?.substring(4))
            }
            break;
          case '/veeva/hco-search/select':
            eventName = 'select';
            eventData = atob(ev?.$link?.queryString?.substring(4))
            break;
          case '/veeva/profile/dcr-submitted':
            eventName = 'dcr-submitted';
            eventData = atob(ev?.$link?.queryString?.substring(4))
            break;
        }
        if (eventName && eventData) {
          eventData = JSON.parse(eventData);
          const myEvent = new CustomEvent(`veeva-network-${eventName}`, {
            detail: eventData,
            bubbles: true,
            cancelable: true,
            composed: false,
          });
          console.log(myEvent);
          window.dispatchEvent(myEvent);
        }
      }
      this.deeplinking.routeWithNavController(this.nav, {
        '/login': LoginPage
      }).subscribe(
        match => {
          console.log(match);
          handleDeeplink(match);
        },
        nomatch => {
          console.log(nomatch);
          handleDeeplink(nomatch);
        }
      );
      // var appToken = "e612d4d1-580c-4dc9-b6cd-f46f15f74452";
      // let options = {};
      // options['Bugsee.AndroidOptions.MonitorNetwork'] = false;
      // options['Bugsee.IOSOptions.ShakeToReport'] = true;
      // (<any>window).Bugsee.launch(appToken, options);
    }

    //Set our window encryption method
    (<any>window).AESGCM = {
      name: "AES-GCM",
      length: 256,
    }

    if (this.loginService.isAuthenticated) {
      this.rootPage = HomePage
    } else {
      this.rootPage = LoginPage;
    }

    this.registerWebMcpTools();
  }

  /**
   * Publish the client @AITool catalogue to a WebMCP-capable browser agent
   * (document.modelContext). Capability-gated — a no-op on the Cordova device
   * webview and in any browser without modelContext, so no other guard is needed.
   */
  private registerWebMcpTools(): void {
    const modelContext = (document as any).modelContext ?? (navigator as any).modelContext;
    if (!modelContext) return;
    for (const tool of this.injector.get(AiToolRegistryService).toWebMcpDescriptors()) {
      modelContext.registerTool(tool);
    }
  }

  @HostListener("window:message", ["$event.data"])
  onMessage(message) {
    if (message.hasOwnProperty("messageSource")) return;
    this.events.publish("window:message", message);
  }
}
